// src/pages/sessions/components/LiveSession/layout/ConnectionBanner.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, SignalSlashIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { ConnectionQuality } from 'livekit-client';

/**
 * Banner that appears when the local user's connection is poor or lost.
 * - Yellow banner for Poor/Unstable (after 5s debounce - controlled by parent)
 * - Red banner for Lost connection
 * Dismissible, but will reappear if quality recovers and drops again.
 */
const ConnectionBanner = ({ quality, isVisible, isUnstable }) => {
  const { t } = useTranslation();
  const [isDismissed, setIsDismissed] = useState(false);
  const previousQualityRef = useRef(quality);
  const previousUnstableRef = useRef(isUnstable);
  
  // Reset dismissed state if quality improves and then drops again
  useEffect(() => {
    const wasGood = [ConnectionQuality.Excellent, ConnectionQuality.Good].includes(previousQualityRef.current);
    const wasNotUnstable = !previousUnstableRef.current;
    const isNowLost = quality === ConnectionQuality.Lost;
    const isNowUnstable = isUnstable;
    
    // Show banner again if:
    // 1. Quality dropped to Lost from good state
    // 2. Became unstable from stable state
    if ((wasGood && isNowLost) || (wasNotUnstable && isNowUnstable)) {
      setIsDismissed(false);
    }
    
    previousQualityRef.current = quality;
    previousUnstableRef.current = isUnstable;
  }, [quality, isUnstable]);
  
  const isLost = quality === ConnectionQuality.Lost;
  
  // Don't show if not visible, dismissed, or neither lost nor unstable
  if (!isVisible || isDismissed || (!isLost && !isUnstable)) {
    return null;
  }
  
  // Determine banner style and content based on severity
  const bannerConfig = isLost ? {
    bgColor: 'bg-red-600',
    Icon: SignalSlashIcon,
    title: t('liveSession.connection.lostTitle', 'Connection lost'),
    description: t('liveSession.connection.lostDescription', 'Attempting to reconnect. Others cannot see or hear you.'),
  } : {
    bgColor: 'bg-yellow-600',
    Icon: ExclamationTriangleIcon,
    title: t('liveSession.connection.poorTitle', 'Your connection is unstable'),
    description: t('liveSession.connection.poorDescription', 'Others may have trouble seeing or hearing you clearly.'),
  };
  
  return (
    <div 
      className={`fixed top-0 left-0 right-0 z-50 ${bannerConfig.bgColor} text-white px-4 py-3 shadow-lg animate-in slide-in-from-top duration-300`}
    >
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <bannerConfig.Icon className="w-6 h-6 flex-shrink-0" />
          <div>
            <p className="font-medium">
              {bannerConfig.title}
            </p>
            <p className="text-sm opacity-90">
              {bannerConfig.description}
            </p>
          </div>
        </div>
        
        <button
          onClick={() => setIsDismissed(true)}
          className="p-1.5 hover:bg-white/20 rounded-full transition-colors flex-shrink-0"
          aria-label={t('common.dismiss', 'Dismiss')}
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default ConnectionBanner;
